import { AnimatedSprite, BLEND_MODES, Rectangle, SCALE_MODES, Sprite, Texture } from "pixi.js"

import { BaseRenderable } from "./base_renderable"
import {
    FIRE_COAL_X, FIRE_COAL_Y, FIRE_FRAME_COUNT, FIRE_FRAME_H, FIRE_FRAME_W, FIRE_HALO_X, FIRE_HALO_Y, FireStyle,
    fireHalo, fireHaloBreath, fireHaloFlicker, fireLevel, fireStrip,
} from "./fire_art"
import { flashEnvelope } from "../effects/transient_lights"
import type { FirelightSource } from "../effects/living_firelight"

export type { FireStyle } from "./fire_art"

// In-world campfire flame: pixel-art frames generated by fire_art.ts (coals, flame tongues, sparks,
// smoke when rained on) played back through a plain AnimatedSprite over an additive halo that warms
// the pit and the ground. Playback is a texture swap per frame -- no per-frame drawing.
//
// Each (style, level) is ONE texture strip, generated the first time any fire needs it and shared by
// every fire after; a fire that burns down walks through the levels rather than being scaled.

// Where the coals land relative to the fire entity's location. The server puts the fire 3px right of
// and 16px above its campfire (campfire.ts), and the pit art's logs cross 6px above the campfire's
// location (packages/tools/src/campfire/gen.py: the art's top is drawn at location.y - 22 and the
// logs cross on its row 16, column 48 = the location's x).
const COAL_OFFSET_X = -3
const COAL_OFFSET_Y = 10
// 15fps at 60Hz: a 24-frame loop lasts 1.6s, and each fire starts on a random frame.
const ANIMATION_SPEED = 0.25
// The halo's brightness at full strength; a dying fire lights less.
const HALO_ALPHA = 0.9
// One flame frame, at the animation's 15fps.
const FRAME_MS = 1000 / (60 * ANIMATION_SPEED)
// Rain makes a fire gutter: its light breathes harder.
const SPUTTER_BREATH = 1.8

// The flare when the fire is fed or lit: up fast, settling over about a second. The bloom is a second,
// larger copy of the halo that swells out of it -- it is what the flare looks like by day, when there
// is no darkness for the light (living_firelight.ts) to push back.
const FLARE_ATTACK_MS = 160
const FLARE_DECAY_MS = 1100
const BLOOM_ALPHA = 0.55
const BLOOM_GROW = 0.45

const strips = new Map<string, Texture[]>()
const framesFor = (style: FireStyle, level: number): Texture[] => {
    const key = `${style}:${level}`
    let frames = strips.get(key)
    if (!frames) {
        const { rgba, width, height } = fireStrip(style, level)
        const base = Texture.fromBuffer(rgba, width, height, { scaleMode: SCALE_MODES.NEAREST }).baseTexture
        frames = Array.from({ length: FIRE_FRAME_COUNT },
            (_, f) => new Texture(base, new Rectangle(f * FIRE_FRAME_W, 0, FIRE_FRAME_W, FIRE_FRAME_H)))
        strips.set(key, frames)
    }
    return frames
}

let haloTexture: Texture | undefined
const halo = (): Texture => {
    if (!haloTexture) {
        const { rgba, width, height } = fireHalo()
        haloTexture = Texture.fromBuffer(rgba, width, height, { scaleMode: SCALE_MODES.LINEAR })
    }
    return haloTexture
}

export class FireSprite extends BaseRenderable implements FirelightSource {
    private animation: AnimatedSprite
    private glow: Sprite
    private bloom: Sprite
    private frameChangedMs = 0
    private flareStartMs?: number
    private style: FireStyle = "campfire"
    private intensity = 1
    private burn = 1
    private level: number

    constructor() {
        super()
        // Sort just above the pit it burns in: the two share a z (the campfire's tile offset cancels
        // the fire's 16px lift), and the flame has to win that tie.
        this.defaultZIndex = 1
        this.glow = new Sprite(halo())
        this.glow.blendMode = BLEND_MODES.ADD
        this.glow.position.set(COAL_OFFSET_X - FIRE_HALO_X, -FIRE_HALO_Y)
        this.addChild(this.glow)
        // Pivoted on the halo's own centre so it swells in place around the coals.
        this.bloom = new Sprite(halo())
        this.bloom.blendMode = BLEND_MODES.ADD
        this.bloom.pivot.set(FIRE_HALO_X, FIRE_HALO_Y)
        this.bloom.position.set(COAL_OFFSET_X, 0)
        this.bloom.visible = false
        this.addChild(this.bloom)

        this.level = fireLevel(1)
        const frames = framesFor(this.style, this.level)
        this.animation = new AnimatedSprite(frames)
        this.animation.position.set(COAL_OFFSET_X - FIRE_COAL_X, -FIRE_COAL_Y)
        this.animation.animationSpeed = ANIMATION_SPEED
        this.animation.onFrameChange = frame => {
            this.frameChangedMs = performance.now()
            this.updateGlow(frame)
        }
        // Start on a random frame so neighbouring fires don't flicker in lockstep.
        this.animation.gotoAndPlay(Math.floor(Math.random() * frames.length))
        this.addChild(this.animation)
        this.updateGlow(this.animation.currentFrame)
    }

    setSputtering = (enabled: boolean) => {
        const style: FireStyle = enabled ? "sputtering" : "campfire"
        if (this.style === style) return
        this.style = style
        this.refresh(true)
    }

    // Fire strength (0..1) for a caller that animates it directly (the forager tutorial's diorama).
    setIntensity = (strength: number) => {
        this.intensity = Math.max(0, Math.min(1, strength))
        this.refresh()
    }

    // The campfire drives its burn-down through the entity's spriteScale (1 -> 0 as the fire ages).
    // A dying fire is drawn as a lower flame at the same pixel size -- the container is never scaled.
    updateScale(scale: number) {
        this.burn = Number.isFinite(scale) ? Math.max(0, Math.min(1, scale)) : 1
        this.scale.set(1, 1)
        this.refresh()
    }

    // The coals are seated by the child offsets above, so the container sits on the location itself.
    getYOffset() {
        return COAL_OFFSET_Y
    }

    /** Where the flame is in its flicker, -1..1, smooth between frames. Drives the firelight's breath. */
    breath(nowMs: number = performance.now()): number {
        const into = Math.max(0, Math.min(1, (nowMs - this.frameChangedMs) / FRAME_MS))
        return fireHaloBreath(this.animation.currentFrame + into) * (this.style === "sputtering" ? SPUTTER_BREATH : 1)
    }

    /** Fed or lit: flare. Restarts one already running -- two logs in quick succession flare twice. */
    flare(nowMs: number = performance.now()) {
        this.flareStartMs = nowMs
    }

    flareLevel(nowMs: number = performance.now()): number {
        if (this.flareStartMs === undefined) return 0
        const level = flashEnvelope(nowMs - this.flareStartMs, FLARE_ATTACK_MS, FLARE_DECAY_MS)
        if (level <= 0 && nowMs > this.flareStartMs) this.flareStartMs = undefined
        return level
    }

    /** Per drawn frame (EntityRenderingManager.endFrame): the flare's bloom. */
    tick(nowMs: number = performance.now()) {
        const level = this.flareLevel(nowMs)
        this.bloom.visible = level > 0.003
        if (!this.bloom.visible) return
        this.bloom.scale.set(1 + BLOOM_GROW * level)
        this.bloom.alpha = BLOOM_ALPHA * level
    }

    // Detach from the shared ticker when the fire entity is destroyed (AnimatedSprite.play registers
    // it there); the cached textures are shared and intentionally left alive.
    cleanup() {
        this.animation.stop()
    }

    private strength = () => this.intensity * this.burn

    private refresh(styleChanged = false) {
        const level = fireLevel(this.strength())
        if (level !== this.level || styleChanged) {
            this.level = level
            // Keep the loop phase: a new level mid-flicker shouldn't jump back to frame 0.
            const frame = this.animation.currentFrame
            this.animation.textures = framesFor(this.style, level)
            this.animation.gotoAndPlay(frame)
        }
        this.updateGlow(this.animation.currentFrame)
    }

    private updateGlow(frame: number) {
        const s = this.strength()
        this.glow.alpha = HALO_ALPHA * fireHaloFlicker(frame) * (0.35 + 0.65 * s) * (this.style === "sputtering" ? 0.6 : 1)
    }
}
